import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ZoomIn, ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { servicesContent } from '../data/servicesContent';
import { Lightbox } from './Lightbox';

/**
 * SOULBOARD EXAMPLES — gallery of real Soulboard PDF pages with their
 * original tags (Archetypes / Sacred spaces / Colour aura / Future self /
 * Practices).
 *
 * The stacked preview cards live inside a fixed-size "stage" that is scaled
 * down and centred to fit any viewport width, so the pile never overflows to
 * the right on mobile and stays fully visible on all screen sizes.
 */
const STAGE_W = 700;
const STAGE_H = 660;

const CARD_POSITIONS = [
  { top: '0',     left: '0',     rotate: '0deg' },
  { top: '50px',  left: '320px', rotate: '3deg' },
  { top: '130px', left: '90px',  rotate: '-2deg' },
  { top: '70px',  left: '380px', rotate: '5deg' },
  { top: '170px', left: '200px', rotate: '-3deg' },
];

const SoulboardExamples = () => {
  const { t, language } = useLanguage();
  const c = servicesContent[language];
  const [activeIndex, setActiveIndex] = useState(0);

  const examples = [
    { id: 'archetypes',   titleKey: 'archetypes',   image: '/gallery/archetypes.jpg' },
    { id: 'sacredSpaces', titleKey: 'sacredSpaces', image: '/gallery/sacred_spaces.jpg' },
    { id: 'colourAura',   titleKey: 'colourAura',   image: '/gallery/img1.jpg' },
    { id: 'futureSelf',   titleKey: 'futureSelf',   image: '/gallery/future_self.jpg' },
    { id: 'practices',    titleKey: 'practices',    image: '/gallery/img6.jpg' },
  ];

  return (
    <section
      id="soulboard-examples"
      className="pt-10 lg:pt-12 pb-24 bg-[#faf7ee]/40 overflow-hidden"
      data-testid="soulboard-examples-section"
    >
      <div className="container mx-auto px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            {/* LEFT: Title + tags + active page */}
            <motion.div
              className="lg:col-span-5 space-y-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <p className="text-[11px] tracking-[0.4em] uppercase text-neutral-500">
                {c.pillarsLabel}
              </p>
              <h2
                className="leading-[0.95] text-neutral-900"
                style={{
                  fontWeight: 500,
                  letterSpacing: '-0.02em',
                  fontSize: 'clamp(2rem, 4vw, 3.25rem)',
                }}
              >
                {t('examplesTitle')}
              </h2>
              <p className="text-base text-neutral-700 leading-relaxed max-w-md">
                {c.insideIntro}
              </p>

              {/* Four content pillars — compact expandable rows */}
              <PillarAccordion pillars={c.pillars} />

              {/* Tag chips */}
              <div className="pt-4">
                <div className="flex flex-wrap gap-2">
                  {examples.map((ex, i) => (
                    <motion.button
                      key={ex.id}
                      onClick={() => setActiveIndex(i)}
                      data-testid={`example-${ex.id}-btn`}
                      className={`text-xs lg:text-sm uppercase font-bold tracking-wider px-4 py-2 border-2 transition-all duration-300 cursor-pointer ${
                        activeIndex === i
                          ? 'border-transparent shadow-lg'
                          : 'border-neutral-300 bg-white text-neutral-800 hover:border-neutral-500'
                      }`}
                      style={activeIndex === i ? { background: '#d4ff00', color: '#171717' } : {}}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {t(ex.titleKey)}
                    </motion.button>
                  ))}
                </div>
              </div>

              {/* Active example caption */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIndex}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.3 }}
                  className="pt-2 flex items-baseline gap-3"
                >
                  <span className="text-[10px] tracking-[0.4em] text-neutral-500 uppercase">
                    Page · {String(activeIndex + 1).padStart(2, '0')}
                  </span>
                  <span className="font-serif text-xl text-neutral-900">
                    {t(examples[activeIndex].titleKey)}
                  </span>
                </motion.div>
              </AnimatePresence>
            </motion.div>

            {/* RIGHT: Stacked image cards (scaled + centred, never overflows) */}
            <div className="lg:col-span-7">
              <ExampleStack
                examples={examples}
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                t={t}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* Four content pillars — compact editorial accordion (no large cards) */
const PillarAccordion = ({ pillars }) => {
  const [open, setOpen] = useState(0);
  return (
    <div className="border-t border-neutral-300 pt-1" data-testid="four-pillars">
      {pillars.map((p, i) => (
        <div key={p.title} className="border-b border-neutral-300" data-testid={`pillar-${i}`}>
          <button
            type="button"
            onClick={() => setOpen(open === i ? -1 : i)}
            className="w-full flex items-center justify-between gap-3 py-3.5 text-left"
          >
            <span className="text-sm lg:text-base font-bold tracking-wide text-neutral-900">{p.title}</span>
            <ChevronDown
              size={16}
              className={`flex-shrink-0 text-neutral-500 transition-transform duration-300 ${open === i ? 'rotate-180' : ''}`}
            />
          </button>
          <AnimatePresence initial={false}>
            {open === i && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <p className="pb-4 text-sm text-neutral-700 leading-relaxed">{p.body}</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

/* Scales a fixed-size design "stage" down to fit the available width and
   centres it, so the overlapping pile is fully visible with zero overflow. */
const useFitScale = (stageWidth) => {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(0.5);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setScale(Math.min(1, el.clientWidth / stageWidth));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [stageWidth]);

  return { wrapRef, scale };
};

const ExampleStack = ({ examples, activeIndex, setActiveIndex, t }) => {
  const { wrapRef, scale } = useFitScale(STAGE_W);
  const startX = useRef(null);
  const swiped = useRef(false);
  const [zoom, setZoom] = useState(null);

  const onPointerDown = (e) => {
    startX.current = e.clientX;
    swiped.current = false;
  };
  const onPointerUp = (e) => {
    if (startX.current == null) return;
    const dx = e.clientX - startX.current;
    startX.current = null;
    if (Math.abs(dx) > 55) {
      swiped.current = true;
      setActiveIndex((p) =>
        dx < 0 ? (p + 1) % examples.length : (p - 1 + examples.length) % examples.length
      );
    }
  };

  return (
    <>
    <div
      ref={wrapRef}
      className="relative w-full overflow-hidden"
      style={{ height: STAGE_H * scale, touchAction: 'pan-y' }}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      data-testid="example-stack"
    >
      <div
        className="absolute top-0"
        style={{
          left: '50%',
          marginLeft: -(STAGE_W * scale) / 2,
          width: STAGE_W,
          height: STAGE_H,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        <AnimatePresence mode="sync">
          {examples.map((ex, index) => {
            const isActive = index === activeIndex;
            const pos = CARD_POSITIONS[index % CARD_POSITIONS.length];
            return (
              <motion.div
                key={ex.id}
                onClick={() => {
                  if (swiped.current) {
                    swiped.current = false;
                    return;
                  }
                  if (isActive) setZoom({ src: ex.image, alt: t(ex.titleKey) });
                  else setActiveIndex(index);
                }}
                data-testid={`example-${ex.id}-card`}
                className={`absolute w-80 h-[480px] bg-white shadow-2xl border border-neutral-200 overflow-hidden ${isActive ? 'cursor-zoom-in' : 'cursor-pointer'}`}
                initial={false}
                animate={{
                  zIndex: isActive ? 100 : 5 - index,
                  top: isActive ? '20px' : pos.top,
                  left: isActive ? '140px' : pos.left,
                  rotate: isActive ? '0deg' : pos.rotate,
                  scale: isActive ? 1.05 : 1,
                }}
                transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
                whileHover={{ scale: isActive ? 1.05 : 1.02 }}
              >
                <img
                  src={ex.image}
                  alt={t(ex.titleKey)}
                  className="w-full h-[85%] object-cover object-top select-none"
                  loading="lazy"
                  draggable={false}
                  onDragStart={(e) => e.preventDefault()}
                />
                <div className="absolute bottom-0 left-0 right-0 bg-white p-3 border-t border-neutral-200">
                  <p
                    className={`text-sm font-medium uppercase tracking-wide ${
                      isActive ? 'text-neutral-900' : 'text-neutral-500'
                    }`}
                  >
                    {t(ex.titleKey)}
                  </p>
                  {isActive && (
                    <motion.div
                      className="w-8 h-1 bg-[#d4ff00] mt-2"
                      initial={{ width: 0 }}
                      animate={{ width: 32 }}
                      transition={{ duration: 0.3 }}
                    />
                  )}
                </div>
                {isActive && (
                  <motion.div
                    className="absolute top-3 right-3 flex items-center justify-center w-8 h-8 rounded-full bg-black/45 backdrop-blur-sm text-white pointer-events-none"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ZoomIn size={15} strokeWidth={2} />
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>
      </div>
    </div>
    <Lightbox src={zoom?.src} alt={zoom?.alt} onClose={() => setZoom(null)} />
    </>
  );
};

export default SoulboardExamples;
